// Mock data for InstantDrive landing site (frontend-only teaser)
import {
  Car, Bike, Users, Crown, ShieldCheck, Wallet, MapPin, Clock,
  Star, PhoneCall, BadgeCheck, TrendingUp, Headphones, Zap, Route,
} from "lucide-react";

export const IMAGES = {
  logo: "/assets/logo-full.png",
  heroPassenger:
    "https://images.pexels.com/photos/4606343/pexels-photo-4606343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  phoneMap:
    "https://images.unsplash.com/photo-1759256243611-502772ac391b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxwaG9uZSUyMG1hcHxlbnwwfHx8fDE3ODk5MjI4NDJ8MA&ixlib=rb-4.1.0&q=85",
  driverMan:
    "https://images.pexels.com/photos/22669772/pexels-photo-22669772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  driverWoman:
    "https://images.pexels.com/photos/7144214/pexels-photo-7144214.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cabPhone:
    "https://images.pexels.com/photos/5835256/pexels-photo-5835256.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  handMap:
    "https://images.unsplash.com/photo-1548345680-f5475ea5df84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxwaG9uZSUyMG1hcHxlbnwwfHx8fDE3ODk5MjI4NDJ8MA&ixlib=rb-4.1.0&q=85",
  cityStreet:
    "https://images.pexels.com/photos/8608890/pexels-photo-8608890.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cityAerial:
    "https://images.pexels.com/photos/38784178/pexels-photo-38784178.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cityNight:
    "https://images.pexels.com/photos/10410202/pexels-photo-10410202.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  taxiPhone:
    "https://images.pexels.com/photos/13801674/pexels-photo-13801674.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export const NAV_LINKS = [
  { label: "How it works", href: "#how" },
  { label: "Ride options", href: "#rides" },
  { label: "Features", href: "#features" },
  { label: "Register", href: "#signup" },
  { label: "Cities", href: "#cities" },
  { label: "FAQ", href: "#faq" },
];

// Launch date — 1 November 2026 (Lagos time / WAT, UTC+1)
export const LAUNCH_DATE = "2026-11-01T00:00:00+01:00";
export const LAUNCH_LABEL = "November 1, 2026";

// App available to download from 1 December 2026
export const APP_AVAILABLE_LABEL = "December 1, 2026";

export const STATS = [
  { value: "2", label: "Cities at launch", suffix: "" },
  { value: "3", label: "Minute average pickup", suffix: "min" },
  { value: "100", label: "Cashless & card ready", suffix: "%" },
  { value: "24/7", label: "In-app support", suffix: "" },
];

export const RIDE_OPTIONS = [
  {
    id: "instant-go",
    icon: Car,
    name: "Instant Go",
    tagline: "Everyday affordable rides",
    desc: "Clean, comfortable saloon cars for your daily commute across the city — without the surge.",
    price: "From ₦1,200",
    eta: "3-5 min",
    seats: "4 seats",
    popular: false,
  },
  {
    id: "instant-comfort",
    icon: Crown,
    name: "Instant Comfort",
    tagline: "Extra legroom, top-rated drivers",
    desc: "Newer vehicles, more space and our highest-rated drivers for a premium experience.",
    price: "From ₦2,400",
    eta: "4-6 min",
    seats: "4 seats",
    popular: true,
  },
  {
    id: "instant-xl",
    icon: Users,
    name: "Instant XL",
    tagline: "Room for the whole crew",
    desc: "Spacious SUVs and MPVs for groups, luggage and airport runs to Lagos & Abuja airports.",
    price: "From ₦3,600",
    eta: "6-9 min",
    seats: "6 seats",
    popular: false,
  },
  {
    id: "instant-bike",
    icon: Bike,
    name: "Instant Bike",
    tagline: "Beat the traffic",
    desc: "Fast, helmet-provided bike rides to zip through Lagos traffic and reach anywhere quickly.",
    price: "From ₦700",
    eta: "2-3 min",
    seats: "1 seat",
    popular: false,
  },
];

export const HOW_STEPS = [
  {
    icon: MapPin,
    title: "Set your destination",
    desc: "Open the app, enter where you're going and see an upfront price before you book.",
  },
  {
    icon: Route,
    title: "Match with a driver",
    desc: "We instantly pair you with the closest verified driver and share live arrival time.",
  },
  {
    icon: Zap,
    title: "Ride smart, go further",
    desc: "Track your trip in real time, pay cashless or by card, and rate your driver at the end.",
  },
];

export const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Safety first",
    desc: "Verified drivers, live trip sharing, SOS button and 24/7 incident response built in.",
  },
  {
    icon: Wallet,
    title: "Cashless & card",
    desc: "Pay with card, wallet or cash. Transparent, upfront pricing with no hidden surge shocks.",
  },
  {
    icon: Clock,
    title: "Fast pickups",
    desc: "Smart matching pairs you with nearby drivers so you're moving in minutes, not hours.",
  },
  {
    icon: BadgeCheck,
    title: "Verified drivers",
    desc: "Every partner passes document checks and background screening before their first trip.",
  },
  {
    icon: Headphones,
    title: "Real human support",
    desc: "Reach a real person any time, day or night, right from inside the InstantDrive app.",
  },
  {
    icon: TrendingUp,
    title: "Fair for drivers",
    desc: "Just 7.5% commission — drivers keep 92.5% of every fare (before tax), with instant daily payouts.",
  },
];

export const DRIVER_PERKS = [
  { icon: Wallet, title: "Keep 92.5%", text: "Just 7.5% commission — you keep 92.5% of every fare (before tax), paid out daily." },
  { icon: Clock, title: "Your schedule", text: "Go online whenever you want — full-time or a few hours a day." },
  { icon: ShieldCheck, title: "Backed up", text: "Insurance options, in-app support and an SOS line on every trip." },
  { icon: Star, title: "Grow ratings", text: "Build a reputation and unlock higher-earning ride categories." },
];

export const CITIES = [
  {
    name: "Lagos",
    status: "Launching soon",
    blurb: "From Ikeja to Lekki — beat the traffic across mainland and island.",
    image: IMAGES.cityStreet,
  },
  {
    name: "Abuja",
    status: "Launching soon",
    blurb: "Smooth rides across Maitama, Wuse, Garki and the wider FCT.",
    image: IMAGES.cityNight,
  },
  {
    name: "Port Harcourt",
    status: "Coming next",
    blurb: "On our roadmap as we expand InstantDrive across Nigeria.",
    image: IMAGES.cityAerial,
  },
];

export const TESTIMONIALS = [
  {
    name: "Chidinma O.",
    role: "Early rider, Lagos",
    rating: 5,
    text: "Finally a ride app that shows the real price upfront. Pickup was fast and the driver was so professional.",
  },
  {
    name: "Musa A.",
    role: "Driver partner, Abuja",
    rating: 5,
    text: "The commission is fair and payouts hit my account daily. InstantDrive actually treats drivers with respect.",
  },
  {
    name: "Tolu B.",
    role: "Early rider, Lagos",
    rating: 5,
    text: "Instant Bike got me through Third Mainland traffic in record time. The helmet and safety features are a big plus.",
  },
  {
    name: "Grace E.",
    role: "Driver partner, Lagos",
    rating: 5,
    text: "Support is real humans, not bots. Any issue on a trip gets sorted quickly. Best decision joining early.",
  },
];

export const FAQS = [
  {
    q: "When is InstantDrive launching?",
    a: "InstantDrive launches on November 1, 2026, starting in Lagos and Abuja. Registration is open now — sign up as a rider or driver today to be ready from day one.",
  },
  {
    q: "How much does a ride cost?",
    a: "You'll always see an upfront, all-in price before you confirm — no hidden charges and no surprise surge. Prices vary by distance, time and ride option.",
  },
  {
    q: "How do I pay for my ride?",
    a: "InstantDrive supports card, in-app wallet and cash. You choose your preferred method in the app before or after the trip.",
  },
  {
    q: "Is InstantDrive safe?",
    a: "Yes. Every driver is verified with document and background checks. Every trip includes live sharing, an SOS button and 24/7 support.",
  },
  {
    q: "How do I sign up to drive?",
    a: "Tap 'Become a driver', register your details and upload your documents. Once verified, you can go online and start earning.",
  },
  {
    q: "Which app should I download?",
    a: "Use the InstantDrive rider app to book rides and the InstantDrive driver app to earn. Both are available on the App Store and Google Play at launch.",
  },
];

export const CONTACT = {
  email: "info@instantdrive.tech",
  site: "www.instantdrive.tech",
  location: "Lagos, Abuja, Nigeria",
  tagline: "Ride Smart. Go Further.",
  phone: "+234 800 000 0000",
};

// App store links (placeholder / mock until real listings go live)
export const APP_LINKS = {
  ios: "https://www.apple.com/app-store/",
  android: "https://play.google.com/store/apps",
};

export const PARTNERS = [
  "Verified Drivers", "Upfront Pricing", "24/7 Support", "SOS Safety",
  "Cashless Rides", "Instant Payouts", "Live Tracking", "No Surge Shocks",
];
